import { useState, useEffect } from 'react';
import { getSettings } from './api';

const DEFAULT_BRANDING = {
  logo_main_url: '/assets/winluxor-logo.png',
  logo_small_url: '/assets/winluxor-icon.png',
  favicon_url: '/assets/winluxor-icon.png',
  site_title: 'WINLUXOR — Enter Your Dream',
};

let cachedBranding = null;
let listeners = [];

const notifyListeners = (branding) => listeners.forEach((cb) => cb(branding));

const fetchBranding = async () => {
  try {
    const r = await getSettings();
    const b = r.data.settings?.branding || {};
    const resolved = {
      logo_main_url: b.logo_main_url || DEFAULT_BRANDING.logo_main_url,
      logo_small_url: b.logo_small_url || DEFAULT_BRANDING.logo_small_url,
      favicon_url: b.favicon_url || DEFAULT_BRANDING.favicon_url,
      site_title: b.site_title || DEFAULT_BRANDING.site_title,
    };
    cachedBranding = resolved;
    notifyListeners(resolved);
    return resolved;
  } catch (e) {
    cachedBranding = DEFAULT_BRANDING;
    notifyListeners(DEFAULT_BRANDING);
    return DEFAULT_BRANDING;
  }
};

/** Apply favicon + page title globally */
export const applyBrandingToDocument = (b) => {
  // Title
  const title = b.site_title || DEFAULT_BRANDING.site_title;
  document.title = title;
  window.__winluxor_title__ = title; // shared with index.js title-lock
  // Favicon
  const favHref = b.favicon_url || DEFAULT_BRANDING.favicon_url;
  document.querySelectorAll('link[rel*="icon"]').forEach((el) => {
    el.setAttribute('href', favHref);
  });
};

/**
 * React hook to access current branding.
 * Returns: { logo_main_url, logo_small_url, favicon_url, site_title, refresh }
 */
export function useBranding() {
  const [branding, setBranding] = useState(cachedBranding || DEFAULT_BRANDING);

  useEffect(() => {
    listeners.push(setBranding);
    if (!cachedBranding) {
      fetchBranding().then((b) => applyBrandingToDocument(b));
    } else {
      applyBrandingToDocument(cachedBranding);
    }
    return () => {
      listeners = listeners.filter((l) => l !== setBranding);
    };
  }, []);

  return { ...branding, refresh: fetchBranding };
}

/** Force reload (e.g. after admin saves). */
export const refreshBranding = async () => {
  const b = await fetchBranding();
  applyBrandingToDocument(b);
  return b;
};
